/**
 * Closed-loop reply panel — send an email back to the end user and view the
 * audit log of prior messages. Replies are only allowed when the reporter
 * opted in AND left an email; otherwise the form is disabled with an
 * explanation (the backend enforces this too).
 *
 * Visual: the Claude Design `.side-sec` "Reply to reporter" block — a tinted
 * `.email-reply` compose surface (To line · subject · body · Send email) over a
 * compact inbound/outbound message log.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import type { Ticket } from '@hotline/contracts';
import type { EmailLog } from '@hotline/client-core';

import { useAuth } from '../auth/AuthContext.js';
import { useOrgScope } from '../org/OrgScopeContext.js';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Spinner,
  Textarea,
  cn,
  type BadgeTone,
} from './ui/index.js';
import { Icon } from './ui/icons.js';
import { useAsync, toMessage } from '../lib/useAsync.js';

/** Direction/status → badge tone. */
const DIRECTION_TONE: Record<string, BadgeTone> = {
  outbound: 'primary',
  inbound: 'info',
};
const STATUS_TONE: Record<string, BadgeTone> = {
  sent: 'ok',
  delivered: 'ok',
  failed: 'danger',
  bounced: 'danger',
  queued: 'warn',
};

export function ReplyPanel({ ticket }: { ticket: Ticket }): ReactElement {
  const { client } = useAuth();
  const { query } = useOrgScope();
  const canReply = Boolean(ticket.emailOptIn && ticket.endUserEmail);

  const emails = useAsync<EmailLog[]>(
    () => client.api.listTicketEmails(ticket.id, query),
    [client, ticket.id, query.orgId],
  );

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const send = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canReply) return;
    const trimmed = body.trim();
    if (!trimmed) return;
    setSending(true);
    setError(null);
    try {
      const log = await client.api.replyToTicket(
        ticket.id,
        { body: trimmed, subject: subject.trim() || undefined },
        query,
      );
      emails.setData((prev) => [...(prev ?? []), log]);
      setBody('');
      setSubject('');
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSending(false);
    }
  };

  const log = emails.data ?? [];

  return (
    <Card>
      <Card.Body className="flex flex-col gap-3.5">
        <h2 className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-faint">
          Closed-loop reply
        </h2>

        {canReply ? (
          <p className="text-[12.5px] text-muted">
            Replying to{' '}
            <strong className="mono font-semibold text-text">
              {ticket.endUserEmail}
            </strong>
          </p>
        ) : (
          <p className="text-[12.5px] text-muted">
            {ticket.endUserEmail
              ? 'The reporter did not opt in to email replies.'
              : 'No reporter email on this ticket.'}
          </p>
        )}

        {emails.loading ? <Spinner /> : null}
        {emails.error ? <Alert tone="danger">{emails.error}</Alert> : null}

        {log.length > 0 ? (
          <ul className="flex flex-col gap-2.5">
            {log.map((m) => (
              <li
                key={m.id}
                data-direction={m.direction}
                className="flex flex-col gap-1 rounded-md border border-border bg-surface-2 p-2.5"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={DIRECTION_TONE[m.direction] ?? 'neutral'} square>
                    {m.direction}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-text">
                    {m.subject}
                  </span>
                  <Badge tone={STATUS_TONE[m.status] ?? 'neutral'} square>
                    {m.status}
                  </Badge>
                </div>
                <p className="text-[12.5px] leading-[1.5] text-muted">
                  {m.body}
                </p>
                <time
                  dateTime={m.sentAt}
                  className="text-[11px] text-faint"
                >
                  {new Date(m.sentAt).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
        ) : null}

        <form
          className={cn(
            'flex flex-col gap-2.5 rounded-md border border-border bg-surface-2 p-[13px]',
          )}
          onSubmit={send}
          aria-label="Send reply"
        >
          <div className="flex items-center gap-[7px] text-xs text-muted [&_svg]:size-3.5">
            <Icon.mail />
            To{' '}
            <b className="mono font-semibold text-text">
              {ticket.endUserEmail || '—'}
            </b>
          </div>
          <Input
            aria-label="Subject"
            placeholder="Subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={!canReply}
            className="bg-surface"
          />
          <Textarea
            aria-label="Reply body"
            placeholder="Write your reply to the user…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            disabled={!canReply}
            className="min-h-[92px] bg-surface"
          />
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <Button
            type="submit"
            size="sm"
            disabled={!canReply || sending || !body.trim()}
          >
            <Icon.send />
            {sending ? 'Sending…' : 'Send reply'}
          </Button>
        </form>
      </Card.Body>
    </Card>
  );
}
